import * as i2 from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject } from '@angular/core';
import * as i1 from '@angular/router';
import { BehaviorSubject, throwError, from } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
const SETTINGS = {
  SILENT_REFRESH_ERROR_CODE: 498,
  EXPIRED_ID_TOKEN_ERROR_CODE: 401,
  ROGERS: {
    LOGOUT_URL: '/consumer/profile/logout?login=true',
    DEEPLINK_STORAGE_KEY: 'ute.rci.deep.link.url'
  },
  FIDO: {
    LOGOUT_URL: '/profile/logout?login=true',
    DEEPLINK_STORAGE_KEY: 'ute.rci.deep.link.url'
  }
};
var BRAND = /*#__PURE__*/function (BRAND) {
  BRAND["FIDO"] = "fido";
  BRAND["ROGERS"] = "rogers";
  return BRAND;
}(BRAND || {});
const OIDC_INTERCEPTOR_CONFIG = new InjectionToken('It provides config for OidcInterceptor. If not provided, default values will be used.');
/* eslint-disable */
let OidcInterceptor = /*#__PURE__*/(() => {
  class OidcInterceptor {
    constructor(router, location, platformId, config) {
      this.router = router;
      this.location = location;
      this.platformId = platformId;
      this.config = config;
      this.isRefreshing = false;
      this.tokenIsRefreshedSubject = new BehaviorSubject(false);
      this.initializeDamSilentAuth();
    }
    static configure(config = {}) {
      function configFactory(platformId) {
        const brand = OidcInterceptor.extractBrandFromUrl(platformId);
        const defaultConfig = {
          silentRefreshErrorCode: SETTINGS.SILENT_REFRESH_ERROR_CODE,
          expiredIdTokenErrorCode: SETTINGS.EXPIRED_ID_TOKEN_ERROR_CODE,
          logoutUrl: brand === BRAND.ROGERS ? SETTINGS.ROGERS.LOGOUT_URL : SETTINGS.FIDO.LOGOUT_URL,
          deeplinkStorageKey: brand === BRAND.ROGERS ? SETTINGS.ROGERS.DEEPLINK_STORAGE_KEY : SETTINGS.FIDO.DEEPLINK_STORAGE_KEY
        };
        return {
          ...defaultConfig,
          ...config
        };
      }
      return {
        provide: OIDC_INTERCEPTOR_CONFIG,
        useFactory: configFactory,
        deps: [PLATFORM_ID]
      };
    }
    // because it's a static function, platFormId should be passed from caller.
    static extractBrandFromUrl(platformId) {
      if (isPlatformBrowser(platformId)) {
        // bypass for ssr as window obj is not available
        return window.location.hostname.includes('fido.ca') ? BRAND.FIDO : BRAND.ROGERS;
      }
    }
    updateConfig(config = {}) {
      return Object.assign(this.config, config);
    }
    intercept(httpRequest, next) {
      if (this.isRefreshing) {
        return this.parkRequests(httpRequest, next);
      }
      return next.handle(httpRequest).pipe(catchError(error => {
        return this.handleError(error, httpRequest, next);
      }));
    }
    handleError(error, httpRequest, next) {
      if (error instanceof HttpErrorResponse && error.status === this.config.silentRefreshErrorCode) {
        return this.handleTokenError(httpRequest, next);
      } else {
        if (error instanceof HttpErrorResponse && error.status === this.config.expiredIdTokenErrorCode) {
          // id token is expired so do NOT trigger silent refresh flow - instead go to sign-in page
          this.logout();
        }
        return throwError(error);
      }
    }
    handleTokenError(httpRequest, next) {
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.tokenIsRefreshedSubject.next(false);
        return from(this.damSilentAuth.triggerSilentTokenRefresh()).pipe(switchMap(result => {
          if (result) {
            this.isRefreshing = false;
            this.tokenIsRefreshedSubject.next(true);
            return next.handle(httpRequest).pipe(catchError(error => {
              return this.handleError(error, httpRequest, next);
            }));
          }
          // if result is false it means silent refresh failed.
          this.logout();
          throw new Error('silent refresh failed');
        }));
      } else {
        return this.parkRequests(httpRequest, next);
      }
    }
    parkRequests(httpRequest, next) {
      return this.tokenIsRefreshedSubject.pipe(filter(isRefreshed => isRefreshed), take(1), switchMap(() => {
        return next.handle(httpRequest);
      }));
    }
    logout() {
      if (isPlatformBrowser(this.platformId)) {
        // bypass for ssr as window obj is not available
        const deeplinkUrl = this.location.prepareExternalUrl(this.router.url);
        window.sessionStorage.setItem(this.config.deeplinkStorageKey, deeplinkUrl);
        window.open(this.config.logoutUrl, '_self');
      }
    }
    initializeDamSilentAuth() {
      try {
        if (isPlatformBrowser(this.platformId)) {
          // bypass for ssr as window obj is not available
          this.damSilentAuth = new window['DamSilentAuth']();
        }
      } catch (error) {
        console.error('dam-silent-auth.js needs to be added in the html header otherwise error 498 cannot be handled!', error);
        this.damSilentAuth = {
          triggerSilentTokenRefresh: () => Promise.resolve(false)
        };
      }
    }
  }
  /** @nocollapse */
  /** @nocollapse */OidcInterceptor.ɵfac = function OidcInterceptor_Factory(t) {
    return new (t || OidcInterceptor)(i0.ɵɵinject(i1.Router), i0.ɵɵinject(i2.Location), i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(OIDC_INTERCEPTOR_CONFIG));
  };
  OidcInterceptor.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: OidcInterceptor,
    factory: OidcInterceptor.ɵfac
  });
  return OidcInterceptor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const oidcInterceptor = {
  provide: HTTP_INTERCEPTORS,
  useClass: OidcInterceptor,
  multi: true
};

/**
 * Generated bundle index. Do not edit.
 */

export { OIDC_INTERCEPTOR_CONFIG, OidcInterceptor, oidcInterceptor };
